import React, { useEffect, useState } from 'react';

export function useLocalStorageState<T>(
  key: string,
  initialValue: T,
  options?: {
    /**
     * Specify a namespace for the key to prevent conflicts, by default it uses the current pathname
     *
     * @default window.location.pathname
     */
    namespace?: string;
  }
): [T, (value: React.SetStateAction<T>) => void] {
  const getNamespace = () => {
    return options?.namespace || window.location.pathname;
  };

  const getValue = () => {
    const namespace = getNamespace();
    const storedValue = localStorage.getItem(`${namespace}_${key}`);
    try {
      return storedValue ? JSON.parse(storedValue) : initialValue;
    } catch (error) {
      console.error(
        `Error retrieving value from localStorage for key "${key}":`,
        error
      );
      return initialValue;
    }
  };

  const [state, setState] = useState<T>(() => {
    return getValue();
  });

  const setValue = (value: React.SetStateAction<T>) => {
    setState(value);
    const namespace = getNamespace();
    localStorage.setItem(`${namespace}_${key}`, JSON.stringify(value));
  };

  useEffect(() => {
    if (options?.namespace) {
      const updatedValue = getValue();
      setValue(updatedValue);
    }
  }, [options?.namespace]);

  return [state, setValue];
}
